// Safe intended-route capture/restoration for the login flow (ADR-0011).
// Only internal, same-origin paths are ever stored or restored; anything else falls
// back to the canonical landing page. Restoration is gated on `/users/auth/me` success by
// the caller — this module only sanitizes and (de)persists the value.

const STORAGE_KEY = 'atlas.intendedRoute'
export const LANDING_ROUTE = '/'

/**
 * A safe internal route: starts with exactly one `/`, is not protocol-relative,
 * has no scheme/host, and no backslash tricks.
 */
export function isSafeInternalPath(path: unknown): path is string {
	if (typeof path !== 'string' || path.length === 0) return false
	if (!path.startsWith('/') || path.startsWith('//') || path.includes('\\')) return false
	// Reject a scheme-like first segment (e.g. `/javascript:…`, `/http://…`).
	if (/^\/[^/?#]*:/.test(path)) return false
	try {
		const base = 'http://internal.invalid'
		return new URL(path, base).origin === base // any host/scheme override → reject
	} catch {
		return false
	}
}

/** Persist the intended route before interactive login (ignores auth-flow paths). */
export function saveIntendedRoute(path: string): void {
	if (!isSafeInternalPath(path)) return
	if (path === '/login' || path.startsWith('/auth/callback')) return
	sessionStorage.setItem(STORAGE_KEY, path)
}

/**
 * Read + clear the intended route, returning a sanitized safe path or the landing
 * page. Call ONLY after `/users/auth/me` has succeeded.
 */
export function takeIntendedRoute(): string {
	const raw = sessionStorage.getItem(STORAGE_KEY)
	sessionStorage.removeItem(STORAGE_KEY)
	return isSafeInternalPath(raw) ? raw : LANDING_ROUTE
}
